import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import images from "../../../assets/images";
import { AuthenticatedAvatar } from "../../authenticated-media/authenticated-avatar";
import { UserAvatar } from "../../user-menu-trigger/user-avatar/user-avatar";
import { useAsyncRoleplaySession } from "../../../hooks/useAsyncRoleplaySession";
import { useAppStore } from "../../../stores/appStore";
import { useUserStore } from "../../../stores/userStore";
import type { AsyncRoleplayMessage } from "../../../types/asyncRoleplay";
import { loadAsyncRoleplaySession } from "../../../utils/asyncRoleplayStorage";
import {
  extractLiveDoctorName,
  normalizeSpeakerName,
} from "../../../utils/transcription";
import {
  AsyncRoleplayAudioPlayer,
  type AsyncRoleplayPlaybackState,
} from "../async-roleplay-audio-player/async-roleplay-audio-player";
import { AsyncRoleplayRecorder } from "../async-roleplay-recorder/async-roleplay-recorder";
import { AsyncRoleplaySessionMeta } from "../async-roleplay-session-panel/async-roleplay-session-meta";
import {
  AsyncRoleplaySessionPanel,
  type AsyncRoleplaySessionStatusLabel,
} from "../async-roleplay-session-panel/async-roleplay-session-panel";
import { AsyncRoleplayTranscriptText } from "../async-roleplay-transcript-text/async-roleplay-transcript-text";
import {
  actionErrorStyles,
  assistantBubbleStyles,
  assistantRowStyles,
  avatarColumnStyles,
  avatarStageStyles,
  bubbleLabelStyles,
  closeButtonStyles,
  dockedControlsStyles,
  emptyStateStyles,
  errorCloseButtonStyles,
  errorContainerStyles,
  finishButtonStyles,
  footerCloseStyles,
  loaderColor,
  loadingContainerStyles,
  messageAvatarStyles,
  messagesListStyles,
  messageStackStyles,
  processingRowStyles,
  profileCardNameStyles,
  profileCardStyles,
  screenRootStyles,
  splitLayoutStyles,
  stageAvatarStyles,
  stagePortraitWrapStyles,
  topBarActionsStyles,
  topBarStyles,
  topBarTitleStyles,
  userBubbleStyles,
  userRowStyles,
} from "./styles";

interface AsyncRoleplayScreenProps {
  sessionId: string;
  onClose: () => void;
}

const formatMessageError = (error: string | null | undefined): string => {
  if (!error) return "Message processing failed.";
  if (error === "message_processing_failed") {
    return "Voice was not detected or could not be processed. Discard and re-record a clearer message, or Retry if this was a temporary error.";
  }
  return error;
};

const sanitizeObjectiveText = (
  raw: string | null | undefined,
  windowTitle?: string | null,
): string => {
  let fromDetails = raw?.trim() || "";
  if (!fromDetails) {
    return "Talk to the HCP, listen to objections, and respond clearly.";
  }

  fromDetails = fromDetails.replace(/\*\*([^*]+)\*\*/g, "$1");
  fromDetails = fromDetails.replace(/\*/g, "");

  const title = windowTitle?.trim();
  if (title && fromDetails.startsWith(title)) {
    fromDetails = fromDetails.slice(title.length).trim();
  }

  fromDetails = fromDetails
    .replace(
      /^Sales Meeting with[\s\S]*?(?=Practice Domain:|Stage:|Objection:|$)/i,
      "",
    )
    .trim();

  fromDetails = fromDetails
    .replace(
      /\s+(Practice Domain:|Stage:|Adoption Sub-Step:|Sub-Step:|Objection:)/gi,
      "\n$1",
    )
    .trim();

  return (
    fromDetails ||
    "Talk to the HCP, listen to objections, and respond clearly."
  );
};

const MessageCard: React.FC<{
  message: AsyncRoleplayMessage;
  isLast: boolean;
  canRetryTranscription: boolean;
  userAvatarSrc?: string | null;
  personaAvatarSrc?: string | null;
  assistantLabel: string;
  onRetryDoctor: () => void;
  onRetryTranscription: () => void;
  onDiscard: () => void;
}> = ({
  message,
  isLast,
  canRetryTranscription,
  userAvatarSrc,
  personaAvatarSrc,
  assistantLabel,
  onRetryDoctor,
  onRetryTranscription,
  onDiscard,
}) => {
  const [assistantPlayback, setAssistantPlayback] =
    useState<AsyncRoleplayPlaybackState>({
      currentTimeSec: 0,
      durationSec: 0,
      isPlaying: false,
    });

  const isOpening = message.message_id === "opening";
  const isDoctorProcessing =
    message.status === "pending" || message.status === "processing";
  const isTranscribing = message.transcription_status === "processing";
  const transcriptionFailed = message.transcription_status === "failed";
  const showUser =
    !!message.user_text ||
    !!message.user_audio_url ||
    isTranscribing ||
    transcriptionFailed ||
    (!isOpening && message.status !== "completed");

  return (
    <Box sx={messageStackStyles}>
      {showUser && (
        <Box sx={userRowStyles}>
          <Box sx={userBubbleStyles}>
            <Typography variant="caption" sx={bubbleLabelStyles}>
              You
            </Typography>
            {message.user_text ? (
              <Typography variant="body2" sx={{ mt: 0.25 }}>
                {message.user_text}
              </Typography>
            ) : isTranscribing ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.25 }}
              >
                Transcribing...
              </Typography>
            ) : transcriptionFailed ? (
              <Box
                sx={{
                  mt: 0.25,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  flexWrap: "wrap",
                }}
              >
                <Typography variant="body2" color="error">
                  Transcription failed
                </Typography>
                {canRetryTranscription && (
                  <Button
                    color="inherit"
                    size="small"
                    onClick={onRetryTranscription}
                    sx={{ minWidth: 0, px: 1 }}
                  >
                    Retry
                  </Button>
                )}
              </Box>
            ) : null}
            <AsyncRoleplayAudioPlayer
              audioUrl={message.user_audio_url}
              label="Your recording"
              variant="user"
            />
          </Box>
          <UserAvatar
            size="32px"
            img={userAvatarSrc || undefined}
            sx={messageAvatarStyles}
          />
        </Box>
      )}

      {(message.assistant_text ||
        message.assistant_audio_url ||
        isDoctorProcessing ||
        isOpening) && (
        <Box sx={assistantRowStyles}>
          <AuthenticatedAvatar
            src={personaAvatarSrc ?? undefined}
            sx={messageAvatarStyles}
          />
          <Box sx={assistantBubbleStyles}>
            <Typography variant="caption" sx={bubbleLabelStyles}>
              {isOpening ? "AI opening" : assistantLabel}
            </Typography>
            {message.assistant_text ? (
              <AsyncRoleplayTranscriptText
                text={message.assistant_text}
                currentTimeSec={assistantPlayback.currentTimeSec}
                durationSec={assistantPlayback.durationSec}
                isPlaying={assistantPlayback.isPlaying}
              />
            ) : isDoctorProcessing ? (
              <Box sx={processingRowStyles}>
                <CircularProgress size={14} sx={{ color: loaderColor }} />
                <Typography variant="body2" color="text.secondary">
                  Generating reply...
                </Typography>
              </Box>
            ) : null}
            <AsyncRoleplayAudioPlayer
              audioUrl={message.assistant_audio_url}
              label="AI reply"
              variant="assistant"
              autoPlay={isLast && message.status === "completed"}
              onPlaybackChange={setAssistantPlayback}
            />
          </Box>
        </Box>
      )}

      {message.status === "failed" && isLast && (
        <Alert
          severity="error"
          sx={{ borderRadius: "10px" }}
          action={
            <Box sx={{ display: "flex", gap: 0.5 }}>
              <Button color="inherit" size="small" onClick={onRetryDoctor}>
                Retry
              </Button>
              {!isOpening && (
                <Button color="inherit" size="small" onClick={onDiscard}>
                  Discard
                </Button>
              )}
            </Box>
          }
        >
          {formatMessageError(message.error)}
        </Alert>
      )}
    </Box>
  );
};

export const AsyncRoleplayScreen: React.FC<AsyncRoleplayScreenProps> = ({
  sessionId,
  onClose,
}) => {
  const listRef = useRef<HTMLDivElement | null>(null);
  const {
    realtimePersona,
    userAvatar,
    realtimeDetailsWindowText,
    domain,
    focus,
  } = useAppStore();
  const { getFocusDisplayName } = useUserStore();
  const focusDisplayName = getFocusDisplayName(focus, domain);
  const [isRecording, setIsRecording] = useState(false);

  const {
    session,
    isLoading,
    isUploading,
    actionError,
    canRecord,
    canFinish,
    timeBudgetExhausted,
    lastMessage,
    maxRecordingSeconds,
    uploadVoice,
    retryUpload,
    hasPendingUpload,
    retryFailedMessage,
    retryTranscription,
    discardFailedMessage,
    finish,
  } = useAsyncRoleplaySession(sessionId);
  const autoFinishOnTimeLimitRef = useRef(false);

  const personaAvatarSrc = useMemo(() => {
    const fromPersona =
      realtimePersona?.avatar ||
      realtimePersona?.avatar_url ||
      realtimePersona?.avatarUrl ||
      null;
    if (fromPersona) return fromPersona;
    const stored = loadAsyncRoleplaySession();
    if (stored?.sessionId === sessionId && stored.personaAvatar) {
      return stored.personaAvatar;
    }
    return images.AudioOnlyAvatar;
  }, [realtimePersona, sessionId]);

  const doctorName = useMemo(() => {
    const fromLive = extractLiveDoctorName(
      session?.window_title,
      session?.persona || realtimePersona?.text,
    );
    if (fromLive) return fromLive;
    if (session?.persona) return normalizeSpeakerName(session.persona);
    if (realtimePersona?.text) return normalizeSpeakerName(realtimePersona.text);
    return "Doctor";
  }, [session?.window_title, session?.persona, realtimePersona]);

  const sessionObjective = useMemo(
    () =>
      sanitizeObjectiveText(
        realtimeDetailsWindowText,
        session?.window_title,
      ),
    [realtimeDetailsWindowText, session?.window_title],
  );

  const sessionStatus: AsyncRoleplaySessionStatusLabel = useMemo(() => {
    if (!session) return "Ready";
    if (session.status === "finishing") return "Finishing";
    if (session.status === "finished") return "Finished";
    if (session.status === "finish_failed") return "Report failed";
    if (timeBudgetExhausted) return "Time limit";
    if (isRecording) return "Recording";
    if (isUploading) return "Uploading";
    if (lastMessage?.transcription_status === "processing") {
      return "Transcribing";
    }
    if (
      lastMessage?.status === "pending" ||
      lastMessage?.status === "processing"
    ) {
      return "Processing";
    }
    return "Ready";
  }, [session, timeBudgetExhausted, isRecording, isUploading, lastMessage]);

  const timeBudgetLabel = useMemo(() => {
    if (!session) return null;
    if (timeBudgetExhausted) return "Time is over";
    const used =
      session.audio_seconds != null
        ? `${Math.round(session.audio_seconds)}s used`
        : null;
    const allowed =
      session.seconds_allowed != null ? `${session.seconds_allowed}s` : null;
    if (used && allowed) return `${used} / ${allowed}`;
    if (used) return used;
    if (allowed) return `Budget ${allowed}`;
    return null;
  }, [session, timeBudgetExhausted]);

  const handleRecordingChange = useCallback((recording: boolean) => {
    setIsRecording(recording);
  }, []);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [session?.messages, isUploading]);

  // Same as pressing Finish when the speech budget is used up.
  useEffect(() => {
    if (!session || session.status !== "active") return;
    if (!timeBudgetExhausted) return;
    if (autoFinishOnTimeLimitRef.current || isUploading) return;
    if (
      lastMessage?.status === "pending" ||
      lastMessage?.status === "processing"
    ) {
      return;
    }

    autoFinishOnTimeLimitRef.current = true;
    void finish().catch((error) => {
      console.error(
        "Failed to auto-finish async roleplay on time limit:",
        error,
      );
      autoFinishOnTimeLimitRef.current = false;
    });
  }, [
    session,
    timeBudgetExhausted,
    isUploading,
    lastMessage?.status,
    finish,
  ]);

  if (isLoading && !session) {
    return (
      <Box sx={loadingContainerStyles}>
        <CircularProgress sx={{ color: loaderColor }} />
      </Box>
    );
  }

  if (!session) {
    return (
      <Box sx={errorContainerStyles}>
        <Alert severity="error">Session not found.</Alert>
        <Button onClick={onClose} sx={errorCloseButtonStyles}>
          Close
        </Button>
      </Box>
    );
  }

  const isReadOnly =
    session.status === "finished" ||
    session.status === "finishing" ||
    session.status === "finish_failed";

  return (
    <Box sx={screenRootStyles}>
      <Box sx={topBarStyles}>
        <Typography sx={topBarTitleStyles}>
          {session.window_title || "Voice Messaging"}
        </Typography>
        <Box sx={topBarActionsStyles}>
          {!isReadOnly && (
            <Button
              variant="contained"
              size="small"
              disabled={!canFinish}
              onClick={() => void finish()}
              sx={finishButtonStyles}
            >
              Finish
            </Button>
          )}
          <IconButton onClick={onClose} aria-label="Close" size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      <Box sx={splitLayoutStyles}>
        <Box sx={avatarColumnStyles}>
          <AsyncRoleplaySessionMeta
            objective={sessionObjective}
            status={sessionStatus}
            domain={domain}
            focusDisplayName={focusDisplayName}
            timeBudgetLabel={timeBudgetLabel}
          />

          <Box sx={avatarStageStyles}>
            <Box sx={stagePortraitWrapStyles}>
              <AuthenticatedAvatar
                src={personaAvatarSrc ?? undefined}
                variant="rounded"
                sx={stageAvatarStyles}
              />
            </Box>
            <Box sx={profileCardStyles}>
              <Typography sx={profileCardNameStyles}>{doctorName}</Typography>
            </Box>
          </Box>
        </Box>

        <AsyncRoleplaySessionPanel
          footer={
            <Box sx={dockedControlsStyles}>
              {actionError && (
                <Alert
                  severity="error"
                  sx={actionErrorStyles}
                  action={
                    hasPendingUpload ? (
                      <Button
                        color="inherit"
                        size="small"
                        onClick={() => void retryUpload()}
                      >
                        Retry upload
                      </Button>
                    ) : undefined
                  }
                >
                  {actionError}
                </Alert>
              )}

              {!isReadOnly && (
                <AsyncRoleplayRecorder
                  docked
                  disabled={!canRecord}
                  isUploading={isUploading}
                  maxSeconds={maxRecordingSeconds}
                  onSend={uploadVoice}
                  onRecordingChange={handleRecordingChange}
                />
              )}

              {isReadOnly && session.status === "finished" && (
                <Box sx={footerCloseStyles}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={onClose}
                    sx={closeButtonStyles}
                  >
                    Close
                  </Button>
                </Box>
              )}
            </Box>
          }
        >
          <Box ref={listRef} sx={messagesListStyles}>
            {session.messages.length === 0 && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={emptyStateStyles}
              >
                Record your first voice message to start the conversation.
              </Typography>
            )}

            {session.messages.map((message, index) => (
              <MessageCard
                key={message.message_id}
                message={message}
                isLast={index === session.messages.length - 1}
                canRetryTranscription={session.status === "active"}
                userAvatarSrc={userAvatar}
                personaAvatarSrc={personaAvatarSrc}
                assistantLabel={doctorName}
                onRetryDoctor={() => void retryFailedMessage()}
                onRetryTranscription={() =>
                  void retryTranscription(message.message_id)
                }
                onDiscard={() => void discardFailedMessage()}
              />
            ))}

            {session.status === "finishing" && (
              <Alert severity="info" icon={<CircularProgress size={16} />}>
                Generating your coaching report...
              </Alert>
            )}

            {session.status === "finish_failed" && (
              <Alert
                severity="error"
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => void finish()}
                  >
                    Retry report
                  </Button>
                }
              >
                {session.error || "Report generation failed."}
              </Alert>
            )}

            {session.status === "finished" && (
              <Alert
                severity="success"
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => {
                      window.dispatchEvent(
                        new CustomEvent("openRecordingsModal"),
                      );
                      onClose();
                    }}
                  >
                    Open Recordings
                  </Button>
                }
              >
                Roleplay finished. Report and statistics are available in
                Recordings.
              </Alert>
            )}
          </Box>
        </AsyncRoleplaySessionPanel>
      </Box>
    </Box>
  );
};
